import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { DeleteOutline } from '@material-ui/icons'
import { Button, DrawerBodyWrapper, DrawerFooterWrapper, Input, MultiSelect, PageLevelTableWrapper, SectionHeader, Table } from '@base'
import { getCategories } from '@data'
import { showLoader } from '@data/state/action'
import { ItemWrapper } from '@pages/Admin/Enquiry/ManageEnquiries/ManageEnquiry/PaymentTerms'
import { ButtonsWrapper } from '@pages/Admin/Enquiry/ManageEnquiries/ManageEnquiry/EditSupplierQuotes/styles'
import { MODAL_TYPES, strings } from '@constants'
import { toggleModal } from '@data/state/action/root'
import { BiInputFieldWrapper } from '../../../../OrgsAndUsers/Organisation/OrgProbe/styles'

const deriveFormState = (data = null) => {
	const state = {
		credit_days: {
			id: 'credit-days',
			label: strings(['credit', 'days']),
			placeholder: strings(['enter', 'credit', 'days']),
			value: '',
			disabled: false,
			required: false,
		},
		credit_limit: {
			id: 'credit-limit',
			label: strings('credit', 'limit'),
			placeholder: strings(['enter', 'credit', 'limit']),
			value: '',
			disabled: false,
			required: false,
		},
		materials_supplied: {
			id: 'materials-supplied',
			label: strings('materials_supplied'),
			placeholder: 'Enter to add new item',
			value: [],
			disabled: false,
			required: false,
		},
		payment_received_days: {
			id: 'payment-received-days',
			label: strings('payment_received_days'),
			placeholder: strings('enter', 'payment_received_days'),
			value: '',
			disabled: false,
			required: false,
		},
		supplier_name: {
			id: 'supplier-name',
			label: strings('supplier_name'),
			placeholder: strings(['enter', 'supplier_name']),
			value: '',
			disabled: false,
			required: false,
		},
	}

	return state
}

const MajorSuppliers = ({ data, callback }) => {
	const dispatch = useDispatch()
	const [supplierList, setSupplierList] = useState(data ?? [])
	const [formState, setFormState] = useState(deriveFormState())
	const [categories, setCategories] = useState([])

	useEffect(() => {
		dispatch(showLoader(true))
		getCategories()
			.then((response) => {
				setCategories((response?.data ?? []).map((item) => ({ key: item?.category_name, label: item?.category_name })))
			})
			.catch((e) => console.log(e))
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [dispatch])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const columns = useMemo(() => {
		const deleteSupplierHandler = (rowData) => {
			dispatch(
				toggleModal(true, MODAL_TYPES.CONFIRMATION, {
					overlayClickable: true,
					title: strings('want_to_delete'),
					confirmAction: (confirmed) => {
						if (confirmed) setSupplierList((_list) => _list.filter((item) => item !== rowData))
						dispatch(toggleModal(false))
					},
				})
			)
		}

		return [
			{ label: strings('supplier_name'), key: 'supplier_name', mobile: true },
			{ label: strings('credit', 'limit'), key: 'credit_limit', mobile: true },
			{ label: strings('credit', 'days'), key: 'credit_days', mobile: true },
			{ label: strings('payment_received_days'), key: 'payment_received_days', mobile: true },
			{ label: strings('materials_supplied'), key: 'materials_supplied', renderer: (data) => data?.join(', '), mobile: true },
			{
				label: '',
				key: (rowData) => rowData,
				mobile: true,
				renderer: (rowData) => (
					<Button xs type='secondary' variant='destructive' margin='0' style={{ minWidth: 0, width: 'fit-content' }} onClick={() => deleteSupplierHandler(rowData)}>
						<DeleteOutline fontSize='small' />
					</Button>
				),
			},
		]
	}, [dispatch])

	const clearState = () => {
		setFormState(deriveFormState())
	}

	const checkAddDisabled = useMemo(() => {
		if (
			!formState?.credit_days?.value ||
			!formState?.credit_limit?.value ||
			!formState?.supplier_name?.value ||
			!formState?.payment_received_days?.value
		) {
			return true
		}
		return false
	}, [formState])

	const addDealDetailHandler = () => {
		const obj = {
			credit_days: formState?.credit_days?.value,
			credit_limit: formState?.credit_limit?.value,
			supplier_name: formState?.supplier_name?.value,
			payment_received_days: formState?.payment_received_days?.value,
			materials_supplied: formState?.materials_supplied?.value?.reduce((acc, item) => [...acc, item?.key], []),
		}
		setSupplierList((_list) => [...(_list ?? []), obj])
		setFormState(deriveFormState())
	}

	const submitHandler = () => {
		callback?.(supplierList)
	}

	const renderForm = () => {
		return (
			<ItemWrapper>
				<SectionHeader></SectionHeader>
				<BiInputFieldWrapper>
					<Input
						id={formState?.credit_days?.id}
						label={formState?.credit_days?.label}
						type='number'
						placeholder={formState?.credit_days?.placeholder}
						value={formState?.credit_days?.value}
						onChange={updateState('credit_days')}
						disabled={formState?.credit_days?.disabled}
						required={formState?.credit_days?.required}
					/>
					<Input
						id={formState?.credit_limit?.id}
						type='number'
						label={formState?.credit_limit?.label}
						placeholder={formState?.credit_limit?.placeholder}
						value={formState?.credit_limit?.value}
						onChange={updateState('credit_limit')}
						disabled={formState?.credit_limit?.disabled}
						required={formState?.credit_limit?.required}
					/>
				</BiInputFieldWrapper>
				<BiInputFieldWrapper>
					<Input
						id={formState?.payment_received_days?.id}
						type='number'
						label={formState?.payment_received_days?.label}
						placeholder={formState?.payment_received_days?.placeholder}
						value={formState?.payment_received_days?.value}
						onChange={updateState('payment_received_days')}
						disabled={formState?.payment_received_days?.disabled}
						required={formState?.payment_received_days?.required}
					/>
					<Input
						id={formState?.supplier_name?.id}
						label={formState?.supplier_name?.label}
						placeholder={formState?.supplier_name?.placeholder}
						value={formState?.supplier_name?.value}
						onChange={updateState('supplier_name')}
						disabled={formState?.supplier_name?.disabled}
						required={formState?.supplier_name?.required}
					/>
				</BiInputFieldWrapper>
				<MultiSelect
					id={formState?.materials_supplied?.id}
					label={formState?.materials_supplied?.label}
					data={categories}
					placeholder={'Enter to add new item'}
					primaryKey={'key'}
					displayKey={'label'}
					value={formState?.materials_supplied?.value || []}
					// required={formState.materials_supplied.required}
					onChange={updateState('materials_supplied')}
					createNewOption={true}
				/>
				<ButtonsWrapper>
					<Button xs margin={`0 0 0 0`} type='secondary' variant='' onClick={clearState}>
						{strings('cancel')}
					</Button>
					<Button xs margin={`0 0 0 auto`} disabled={checkAddDisabled} onClick={addDealDetailHandler}>
						{strings('add')}
					</Button>
				</ButtonsWrapper>
			</ItemWrapper>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				{renderForm()}
				{supplierList?.length > 0 && (
					<PageLevelTableWrapper>
						<Table
							fullHeight
							columns={columns}
							data={supplierList}
							hideFooter
							page={0}
							totalItemCount={supplierList.length}
							rowClickable={false}
						/>
					</PageLevelTableWrapper>
				)}
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button small onClick={submitHandler}>
					{strings('submit')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default MajorSuppliers
